import React, { useState, useEffect } from "react";
import "../pos/pos.css";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { userRequest, publicRequest } from "../../requestMethods";
import { Add, Remove, Close } from "@material-ui/icons";
import { FiShoppingCart } from "react-icons/fi";
import styled from "styled-components";
import Select from "react-select";
import { customSelectStyles } from "../../components/shared/SelectStyles";
import { getCategories } from "../../redux/apiCalls";

const ModalContainer = styled.div`
    height: 100%;
    width: 100%;
    opacity: 0.75;
    background: black;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    position: fixed;
    z-index: 10;
`;

const OverlayContainer = styled.div`
    overflow: hidden;
`;

const Overlay = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    color: white;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    top: 0;
    background-color: rgba(white, 0.5);
    visibility: visible;
`;

const ModalDetails = styled.div`
    background-color: rgba(white, 0.5);
    color: black;
    height: 100%;
    width: 50%;
    display: flex;
    z-index: 102;
    
    @media (max-width: 768px) {
        width: 100%;
    }
`;

const EmptyModalDetails = styled.div`
    background-color: rgba(white, 0.5);
    color: black;
    height: 100%;
    width: 50%;
    display: flex;
    z-index: 102;
    
    @media (max-width: 768px) {
        display: none;
    }
`;

const ModalItemContainer = styled.div`
    background-color: white;
    color: black;
    height: 100%;
    width: 100%;
    display: grid;
`;

const ColorPickerContainer = styled.div`
    overflow-y: auto;
    overflow-x: hidden;
    height: 100vh;
    z-index: 101;
    background: #ffffff;
    
    @media (max-width: 768px) {
        padding-top: 80px;
        height: calc(100vh - 80px);
    }
`;

const NewPurchaseOrder = () => {
    const history = useHistory();
    const dispatch = useDispatch();
    const currentUser = useSelector((state) => state.user.currentUser);
    const categories = useSelector((state) => state.product.categories);
    const [companiesData, setCompaniesData] = useState([]);
    const [materialsCart, setMaterialsCart] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    
    const [formData, setFormData] = useState({
        dateOfIssue: new Date().toISOString().split('T')[0],
        company: "",
        classificationCode: "",
        poNumber: "",
        remarks: "",
    });

    const [addOnModalOpen, setAddOnModalOpen] = useState(false);
    const [addOnForm, setAddOnForm] = useState({ name: "", quantity: "1", price: "0" });

    const [itemDetails, setItemDetails] = useState({});
    const [colorPicker, setColorPicker] = useState(false);
    const [product, setProduct] = useState(null);
    const [color, setColor] = useState([]);
    const [selectedMaterial, setSelectedMaterial] = useState({
        color: null,
        price: 0,
        classificationCode: "",
        company: "",
        quantity: 1,
    });
    const [materialSearch, setMaterialSearch] = useState("");
    const [selectMaterialsModalOpen, setSelectMaterialsModalOpen] = useState(false);
    const [isMobileNPO, setIsMobileNPO] = useState(typeof window !== "undefined" && window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobileNPO(window.innerWidth <= 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const superadmin = currentUser?.isSuperAdmin;
    const outright = currentUser?.type === "outright";
    const manager = currentUser?.type === "manager";
    const purchaser = currentUser?.type === "purchaser";
    const productionManager = currentUser?.type === "productionManager";

    // Access control
    const canCreate = superadmin || manager || outright || purchaser || (productionManager && process.env.REACT_APP_PROJECT === "JINSORA");

    // Dynamic materials data from database (full objects)
    const [materialsData, setMaterialsData] = useState([]);
    
    // Current category materials mapping (filtered by selected category)
    const materialsMapping = {};
    const selectedCategoryTitle = product?.title?.toLowerCase();
    
    materialsData.forEach((material) => {
        const normalizedMaterialTitle = (material?.title || "").trim().toLowerCase();
        const isExcludedMaterialType =
            normalizedMaterialTitle === "others" ||
            normalizedMaterialTitle === "other" ||
            normalizedMaterialTitle === "add on" ||
            normalizedMaterialTitle === "add-on" ||
            normalizedMaterialTitle === "addon";
        if (isExcludedMaterialType) return;

        const materialCategoryTitle = material.category?.title?.toLowerCase();
        // If a category is selected, only show materials from that category
        if (selectedCategoryTitle && materialCategoryTitle === selectedCategoryTitle && material.itemCode) {
            materialsMapping[material.itemCode] = material.title;
        } else if (!selectedCategoryTitle && material.itemCode) {
            // If no category selected, show all materials
            materialsMapping[material.itemCode] = material.title;
        }
    });

    // Reverse mapping: Type to Item Code for Materials
    const typeToItemCodeMaterials = Object.fromEntries(
        Object.entries(materialsMapping).map(([key, value]) => [value, key])
    );

    // Fetch categories
    useEffect(() => {
        getCategories(dispatch, {});
    }, [dispatch]);

    // Fetch materials from database
    useEffect(() => {
        const fetchMaterialsData = async () => {
            try {
                // Build params - filter by principle unless superadmin
                const params = {};
                if (currentUser) {
                    if (currentUser.isSuperAdmin) {
                        // Superadmin can see all materials - no filter
                    } else if (currentUser.type === "outright") {
                        params.principle = currentUser._id;
                    } else if (currentUser.principle) {
                        params.principle = currentUser.principle;
                    }
                }

                const res = await publicRequest.get("/materials/", {
                    params
                });
                setMaterialsData(res.data);
            } catch (err) {
                console.error("Error fetching materials:", err);
            }
        };

        fetchMaterialsData();
    }, [currentUser]);

    useEffect(() => {
        if (canCreate) {
            fetchCompanies();
        }
    }, [canCreate]);

    const fetchCompanies = async () => {
        try {
            const filters = {
                status: "active",
                limit: 9999,
                page: 1,
            };
            const res = await publicRequest.get("/companies", {
                params: {
                    filters: JSON.stringify(filters)
                }
            });
            
            // Filter companies by user's principle
            const allCompanies = res.data?.companies || res.data || [];
            const filteredCompanies = allCompanies.filter((company) => {
                // SuperAdmin can see all companies
                if (superadmin) return true;
                
                // Outright and manager users see companies where principle matches their _id
                if (outright || manager) {
                    return company.principle === currentUser._id;
                }
                
                // Other users see companies where principle matches their principle
                return !currentUser?.principle || company.principle === currentUser.principle;
            });
            
            setCompaniesData(filteredCompanies);
        } catch (err) {
            console.error("Error fetching companies:", err);
        }
    };

    const handleAddMaterial = () => {
        if (!selectedMaterial.color) {
            alert("Please select a material type");
            return;
        }

        const materialToAdd = {
            ...product,
            activeColor: selectedMaterial.color,
            amount: selectedMaterial.quantity,
            code: product.title,
            price: selectedMaterial.price,
            classificationCode: selectedMaterial.classificationCode,
            company: selectedMaterial.company,
            uniqueId: `${selectedMaterial.color?.itemCode || 'unknown'}_${selectedMaterial.color?.type || 'unknown'}_${Date.now()}`,
        };

        setMaterialsCart((prev) => [...prev, materialToAdd]);
        setColorPicker(false);
        setSelectedMaterial({
            color: null,
            price: 0,
            classificationCode: "",
            company: "",
            quantity: 1,
        });
    };

    const handleMaterialQuantity = (material, quantityChange, newAmount) => {
        setMaterialsCart((prev) => {
            if (newAmount <= 0) {
                return prev.filter((item) => item.uniqueId !== material.uniqueId);
            } else {
                return prev.map((item) =>
                    item.uniqueId === material.uniqueId
                        ? { ...item, amount: newAmount }
                        : item
                );
            }
        });
    };

    const handleMaterialPrice = (material, newPrice) => {
        setMaterialsCart((prev) => {
            return prev.map((item) =>
                item.uniqueId === material.uniqueId
                    ? { ...item, price: newPrice }
                    : item
            );
        });
    };

    const handleMaterialClassificationCode = (material, classificationCode) => {
        setMaterialsCart((prev) => {
            return prev.map((item) =>
                item.uniqueId === material.uniqueId
                    ? { ...item, classificationCode: classificationCode }
                    : item
            );
        });
    };

    const handleMaterialCompany = (material, companyId) => {
        setMaterialsCart((prev) => {
            return prev.map((item) =>
                item.uniqueId === material.uniqueId
                    ? { ...item, company: companyId }
                    : item
            );
        });
    };

    const handleMaterialMeasurements = (material, measurements) => {
        setMaterialsCart((prev) => {
            return prev.map((item) =>
                item.uniqueId === material.uniqueId
                    ? { ...item, measurements: measurements }
                    : item
            );
        });
    };

    const openAddOnModal = () => {
        setAddOnForm({ name: "", quantity: "1", price: "0" });
        setAddOnModalOpen(true);
        if (isMobileNPO) setSelectMaterialsModalOpen(false);
    };

    const closeAddOnModal = () => {
        setAddOnModalOpen(false);
        setAddOnForm({ name: "", quantity: "1", price: "0" });
    };

    const handleSubmitAddOn = () => {
        const trimmedName = addOnForm.name.trim();
        if (!trimmedName) {
            alert("Please enter an item name for the Add On.");
            return;
        }
        const parsedQuantity = parseInt(addOnForm.quantity, 10);
        const safeQuantity =
            Number.isFinite(parsedQuantity) && parsedQuantity > 0 ? parsedQuantity : 1;
        const parsedPrice = parseFloat(addOnForm.price);
        const safePrice =
            Number.isFinite(parsedPrice) && parsedPrice >= 0 ? parsedPrice : 0;

        const materialToAdd = {
            title: "Add On",
            activeColor: {
                type: trimmedName,
                itemCode: "",
                price: safePrice,
            },
            amount: safeQuantity,
            code: "",
            itemCode: "",
            type: trimmedName,
            model: trimmedName,
            price: safePrice,
            measurements: "",
            classificationCode: "",
            company: "",
            uniqueId: `addon_${trimmedName.toLowerCase()}_${Date.now()}`,
        };

        setMaterialsCart((prev) => [...prev, materialToAdd]);
        closeAddOnModal();
    };

    const handleSubmit = async () => {
        if (!formData.company) {
            alert("Please select a company");
            return;
        }

        if (!formData.dateOfIssue) {
            alert("Please select a date of issue");
            return;
        }

        if (materialsCart.length === 0) {
            alert("Please add at least one material");
            return;
        }

        setIsLoading(true);

        try {
            const trimmedPoNumber = (formData.poNumber || "").trim();
            const purchaseOrderData = {
                company: formData.company,
                classificationCode: formData.classificationCode,
                dateOfIssue: formData.dateOfIssue,
                createdBy: currentUser._id,
                remarks: formData.remarks,
                ...(trimmedPoNumber !== "" ? { poNumber: trimmedPoNumber } : {}),
                materials: materialsCart.map((item) => ({
                    productId: item._id,
                    quantity: item.amount,
                    code: item.activeColor?.itemCode || item.itemCode || item.code,
                    img: item.img,
                    amount: item.amount,
                    type: item.activeColor?.type || item.color?.type || item.type,
                    itemCode: item.activeColor?.itemCode || item.color?.itemCode || item.itemCode,
                    model: item.activeColor?.name || item.model || item.title,
                    price: item.price || item.activeColor?.price || 0,
                    classificationCode: item.classificationCode || '',
                    company: item.company || '',
                    measurements: item.measurements || '',
                })),
            };

            const res = await userRequest.post("/purchaseOrders", purchaseOrderData);

            if (res.data) {
                alert(`Purchase Order created successfully! PO Number: ${res.data.poNumber}`);
                history.push("/purchaseOrders");
            }
        } catch (err) {
            console.error("Error creating purchase order:", err);
            alert("Failed to create purchase order. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const calculateTotal = () => {
        return materialsCart.reduce((sum, item) => 
            sum + ((item.price || 0) * (item.amount || 0)), 0
        );
    };

    if (!canCreate) {
        return (
            <div className="pos">
                <div className="userContainer">
                    <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
                    <p>Only Superadmin, Manager, Outright, and Purchaser users can create purchase orders.</p>
                </div>
            </div>
        );
    }

    return (
        <>
            {/* Add On Modal - same design as ProductPickerModal */}
            {addOnModalOpen && (
                <div className="product-picker-modal-root" role="dialog" aria-modal="true">
                    <div
                        className="product-picker-backdrop"
                        style={{
                            position: "fixed",
                            inset: 0,
                            background: "black",
                            opacity: 0.75,
                            zIndex: 10,
                        }}
                        onClick={closeAddOnModal}
                        aria-hidden="true"
                    />
                    <div
                        className="product-picker-overlay"
                        style={{
                            position: "fixed",
                            inset: 0,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            zIndex: 11,
                            pointerEvents: "none",
                        }}
                    >
                        <div
                            onClick={closeAddOnModal}
                            style={{
                                height: "100%",
                                width: "50%",
                                pointerEvents: "auto",
                            }}
                        />
                        <div
                            className="product-picker-panel"
                            style={{
                                height: "100%",
                                width: "50%",
                                display: "flex",
                                background: "white",
                                pointerEvents: "auto",
                            }}
                        >
                            <div
                                className="product-picker-panel-inner"
                                style={{
                                    background: "white",
                                    height: "100%",
                                    width: "100%",
                                    display: "grid",
                                }}
                            >
                                <div className="purchase-order-select-materials-modal-header product-picker-mobile-header">
                                    <h3>Add On</h3>
                                    <button
                                        type="button"
                                        className="purchase-order-select-materials-modal-close"
                                        onClick={closeAddOnModal}
                                        aria-label="Close"
                                    >
                                        <Close />
                                    </button>
                                </div>
                                <div
                                    className="product-picker-modal-body"
                                    style={{
                                        overflowY: "auto",
                                        overflowX: "hidden",
                                        height: "100vh",
                                        background: "rgb(240, 240, 255)",
                                    }}
                                >
                                    <div className="itemDetailsContainer">
                                        <button
                                            type="button"
                                            className="close-menu-button"
                                            onClick={closeAddOnModal}
                                            aria-label="Close"
                                        >
                                            <Close />
                                        </button>

                                        <div className="summary">
                                            <div className="addProductItem fullWidth">
                                                <label>Item Name</label>
                                                <input
                                                    name="addOnName"
                                                    type="text"
                                                    autoFocus
                                                    placeholder="Enter item name"
                                                    value={addOnForm.name}
                                                    onChange={(e) =>
                                                        setAddOnForm((prev) => ({
                                                            ...prev,
                                                            name: e.target.value,
                                                        }))
                                                    }
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                            e.preventDefault();
                                                            handleSubmitAddOn();
                                                        }
                                                    }}
                                                />
                                            </div>

                                            <div className="addProductItem fullWidth">
                                                <label>Quantity</label>
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "10px",
                                                    }}
                                                >
                                                    <Remove
                                                        style={{
                                                            fontSize: "1.75rem",
                                                            cursor: "pointer",
                                                            padding: "4px",
                                                            borderRadius: "4px",
                                                            border: "1px solid #d1d5db",
                                                            backgroundColor: "#f9fafb",
                                                        }}
                                                        onClick={() => {
                                                            const current =
                                                                parseInt(addOnForm.quantity, 10) || 1;
                                                            if (current > 1) {
                                                                setAddOnForm((prev) => ({
                                                                    ...prev,
                                                                    quantity: String(current - 1),
                                                                }));
                                                            }
                                                        }}
                                                    />
                                                    <input
                                                        name="quantity"
                                                        type="number"
                                                        min="1"
                                                        placeholder="Quantity"
                                                        value={addOnForm.quantity}
                                                        onChange={(e) => {
                                                            const value =
                                                                parseInt(e.target.value, 10) || 1;
                                                            setAddOnForm((prev) => ({
                                                                ...prev,
                                                                quantity: String(Math.max(1, value)),
                                                            }));
                                                        }}
                                                        style={{
                                                            flex: 1,
                                                            textAlign: "center",
                                                            padding: "8px",
                                                        }}
                                                    />
                                                    <Add
                                                        style={{
                                                            fontSize: "1.75rem",
                                                            cursor: "pointer",
                                                            padding: "4px",
                                                            borderRadius: "4px",
                                                            border: "1px solid #d1d5db",
                                                            backgroundColor: "#f9fafb",
                                                        }}
                                                        onClick={() => {
                                                            const current =
                                                                parseInt(addOnForm.quantity, 10) || 1;
                                                            setAddOnForm((prev) => ({
                                                                ...prev,
                                                                quantity: String(current + 1),
                                                            }));
                                                        }}
                                                    />
                                                </div>
                                            </div>

                                            <div className="addProductItem fullWidth">
                                                <label>Price</label>
                                                <input
                                                    name="price"
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    placeholder="Enter price"
                                                    value={addOnForm.price}
                                                    onChange={(e) =>
                                                        setAddOnForm((prev) => ({
                                                            ...prev,
                                                            price: e.target.value,
                                                        }))
                                                    }
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                            e.preventDefault();
                                                            handleSubmitAddOn();
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        <button
                                            type="button"
                                            className="productListEdit mt-2"
                                            onClick={handleSubmitAddOn}
                                        >
                                            Add To Materials
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Color Picker Modal */}
            {colorPicker && product && (
                <OverlayContainer className="product-picker-modal-root">
                    <ModalContainer
                        className="product-picker-backdrop"
                        onClick={() => {
                            setColorPicker(false);
                            setSelectedMaterial({
                                color: null,
                                price: 0,
                                classificationCode: "",
                                company: "",
                                quantity: 1,
                            });
                        }}
                    ></ModalContainer>
                    <Overlay
                        className="product-picker-overlay"
                        onClick={() => {
                            setColorPicker(false);
                            setSelectedMaterial({
                                color: null,
                                price: 0,
                                classificationCode: "",
                                company: "",
                                quantity: 1,
                            });
                        }}
                    >
                        <EmptyModalDetails></EmptyModalDetails>
                        <ModalDetails className="product-picker-panel" onClick={(e) => e.stopPropagation()}>
                            <ModalItemContainer className="product-picker-panel-inner">
                                {/* Mobile-only header: same look as Select Materials modal for seamless transition */}
                                <div className="purchase-order-select-materials-modal-header product-picker-mobile-header">
                                    <h3>{product?.title || "Item details"}</h3>
                                    <button
                                        type="button"
                                        className="purchase-order-select-materials-modal-close"
                                        onClick={() => {
                                            setColorPicker(false);
                                            setSelectedMaterial({
                                                color: null,
                                                price: 0,
                                                classificationCode: "",
                                                company: "",
                                                quantity: 1,
                                            });
                                        }}
                                        aria-label="Close"
                                    >
                                        <Close />
                                    </button>
                                </div>
                                <ColorPickerContainer className="product-picker-modal-body">
                                    <div style={{ 
                                        padding: "40px",
                                        maxWidth: "800px",
                                        margin: "0 auto",
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "24px"
                                    }}>
                                        {/* ITEM CODE */}
                                        <div>
                                            <label style={{
                                                display: "block",
                                                marginBottom: "8px",
                                                fontSize: "13px",
                                                fontWeight: "600",
                                                color: "#1f2937",
                                                letterSpacing: "0.05em",
                                                textTransform: "uppercase"
                                            }}>
                                                ITEM CODE
                                            </label>
                                            <Select
                                                styles={customSelectStyles}
                                                isSearchable={true}
                                                isClearable={true}
                                                menuPortalTarget={document.body}
                                                menuPosition="fixed"
                                                value={
                                                    selectedMaterial.color?.itemCode
                                                        ? {
                                                              label: selectedMaterial.color.itemCode,
                                                              value: selectedMaterial.color.itemCode,
                                                          }
                                                        : null
                                                }
                                                onChange={(selectedOption) => {
                                                    const itemCode = selectedOption?.value || "";
                                                    const description = materialsMapping[itemCode];
                                                    setSelectedMaterial({
                                                        ...selectedMaterial,
                                                        color: { itemCode, type: description },
                                                    });
                                                }}
                                                options={Object.entries(materialsMapping)
                                                    .filter(([code, description]) => {
                                                        const searchLower = materialSearch.toLowerCase();
                                                        return (
                                                            code.toLowerCase().includes(searchLower) ||
                                                            description.toLowerCase().includes(searchLower)
                                                        );
                                                    })
                                                    .map(([code, description]) => ({
                                                        label: code,
                                                        value: code,
                                                    }))}
                                                placeholder="Select Item Code"
                                            />
                                        </div>

                                        {/* TYPE */}
                                        <div>
                                            <label style={{
                                                display: "block",
                                                marginBottom: "8px",
                                                fontSize: "13px",
                                                fontWeight: "600",
                                                color: "#1f2937",
                                                letterSpacing: "0.05em",
                                                textTransform: "uppercase"
                                            }}>
                                                TYPE
                                            </label>
                                            <Select
                                                styles={customSelectStyles}
                                                isSearchable={true}
                                                isClearable={true}
                                                menuPortalTarget={document.body}
                                                menuPosition="fixed"
                                                value={
                                                    selectedMaterial.color?.type
                                                        ? {
                                                              label: selectedMaterial.color.type,
                                                              value: selectedMaterial.color.type,
                                                          }
                                                        : null
                                                }
                                                onChange={(selectedOption) => {
                                                    const type = selectedOption?.value || "";
                                                    const itemCode = typeToItemCodeMaterials[type];
                                                    setSelectedMaterial({
                                                        ...selectedMaterial,
                                                        color: { itemCode, type },
                                                    });
                                                }}
                                                options={Object.values(materialsMapping)
                                                    .filter((description) => {
                                                        const searchLower = materialSearch.toLowerCase();
                                                        return description.toLowerCase().includes(searchLower);
                                                    })
                                                    .map((type) => ({
                                                        label: type,
                                                        value: type,
                                                    }))}
                                                placeholder="Select Type"
                                            />
                                        </div>

                                        {/* PRICE */}
                                        <div>
                                            <label style={{
                                                display: "block",
                                                marginBottom: "8px",
                                                fontSize: "13px",
                                                fontWeight: "600",
                                                color: "#1f2937",
                                                letterSpacing: "0.05em",
                                                textTransform: "uppercase"
                                            }}>
                                                PRICE
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={selectedMaterial.price}
                                                onChange={(e) => setSelectedMaterial({
                                                    ...selectedMaterial,
                                                    price: parseFloat(e.target.value) || 0
                                                })}
                                                placeholder="Enter price"
                                                style={{
                                                    width: "100%",
                                                    padding: "14px 16px",
                                                    fontSize: "15px",
                                                    border: "1px solid #e5e7eb",
                                                    borderRadius: "12px",
                                                    background: "#f9fafb",
                                                    color: "#1f2937",
                                                    outline: "none"
                                                }}
                                            />
                                        </div>

                                        {/* QUANTITY */}
                                        <div>
                                            <label style={{
                                                display: "block",
                                                marginBottom: "8px",
                                                fontSize: "13px",
                                                fontWeight: "600",
                                                color: "#1f2937",
                                                letterSpacing: "0.05em",
                                                textTransform: "uppercase"
                                            }}>
                                                QUANTITY
                                            </label>
                                            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                                <button
                                                    type="button"
                                                    onClick={() => setSelectedMaterial({
                                                        ...selectedMaterial,
                                                        quantity: Math.max(1, selectedMaterial.quantity - 1)
                                                    })}
                                                    style={{
                                                        width: "48px",
                                                        height: "48px",
                                                        background: "#ef4444",
                                                        color: "white",
                                                        border: "none",
                                                        borderRadius: "10px",
                                                        cursor: "pointer",
                                                        fontSize: "20px",
                                                        fontWeight: "bold",
                                                    }}
                                                >
                                                    −
                                                </button>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={selectedMaterial.quantity}
                                                    onChange={(e) => setSelectedMaterial({
                                                        ...selectedMaterial,
                                                        quantity: Math.max(1, parseInt(e.target.value) || 1)
                                                    })}
                                                    style={{
                                                        flex: 1,
                                                        padding: "14px 16px",
                                                        fontSize: "16px",
                                                        fontWeight: "600",
                                                        textAlign: "center",
                                                        border: "1px solid #e5e7eb",
                                                        borderRadius: "12px",
                                                        background: "#f9fafb",
                                                        outline: "none"
                                                    }}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setSelectedMaterial({
                                                        ...selectedMaterial,
                                                        quantity: selectedMaterial.quantity + 1
                                                    })}
                                                    style={{
                                                        width: "48px",
                                                        height: "48px",
                                                        background: "#10b981",
                                                        color: "white",
                                                        border: "none",
                                                        borderRadius: "10px",
                                                        cursor: "pointer",
                                                        fontSize: "20px",
                                                        fontWeight: "bold",
                                                    }}
                                                >
                                                    +
                                                </button>
                                            </div>
                                        </div>

                                        {/* ADD TO MATERIALS Button */}
                                        <button
                                            type="button"
                                            onClick={handleAddMaterial}
                                            disabled={!selectedMaterial.color}
                                            style={{
                                                width: "100%",
                                                padding: "16px 24px",
                                                background: selectedMaterial.color ? "#059669" : "#9ca3af",
                                                color: "white",
                                                border: "none",
                                                borderRadius: "12px",
                                                fontSize: "15px",
                                                fontWeight: "600",
                                                letterSpacing: "0.05em",
                                                textTransform: "uppercase",
                                                cursor: selectedMaterial.color ? "pointer" : "not-allowed",
                                                marginTop: "16px",
                                                transition: "all 0.2s ease"
                                            }}
                                            onMouseEnter={(e) => {
                                                if (selectedMaterial.color) {
                                                    e.currentTarget.style.background = "#047857";
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (selectedMaterial.color) {
                                                    e.currentTarget.style.background = "#059669";
                                                }
                                            }}
                                        >
                                            ADD TO MATERIALS
                                        </button>
                                    </div>
                                </ColorPickerContainer>
                            </ModalItemContainer>
                        </ModalDetails>
                    </Overlay>
                </OverlayContainer>
            )}

            <div className="pos new-purchase-order-page">
                <div className="userContainer">
                    <div className="checkoutButtons">
                        <div className="salesButton bronze" style={{ width: "100%" }}>
                            Purchase Order
                        </div>
                    </div>
                    <div className="calculator">
                        <div className="cartContainer" style={{ gridTemplateColumns: "2fr 1.5fr 1.2fr 1.4fr 1fr" }}>
                            <div className="cartTitle">Item</div>
                            <div className="cartTitle">Item Code</div>
                            <div className="cartTitle">Price</div>
                            <div className="cartTitle">Measurements</div>
                            <div className="cartTitle">Quantity</div>
                        </div>
                        {materialsCart && materialsCart.length > 0 ? (
                            materialsCart.map((material, i) => (
                                <div className="cartContainer" style={{ gridTemplateColumns: "2fr 1.5fr 1.2fr 1.4fr 1fr" }} key={material.uniqueId || i}>
                                    {/* Item */}
                                    <div className="cartItem capitalize">
                                        {material.activeColor?.type || 'N/A'}
                                    </div>
                                    {/* Item Code */}
                                    <div className="cartItem">
                                        {material.itemCode || material.color?.itemCode || material.activeColor?.itemCode || material.code || 'N/A'}
                                    </div>
                                    {/* Price */}
                                    <div className="cartItem">
                                        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                            <span>RM</span>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={material.price || 0}
                                                onChange={(e) => {
                                                    const newPrice = parseFloat(e.target.value) || 0;
                                                    handleMaterialPrice(material, newPrice);
                                                }}
                                                style={{
                                                    width: "80px",
                                                    padding: "6px 10px",
                                                    border: "1.5px solid #e5e7eb",
                                                    borderRadius: "6px",
                                                    fontSize: "14px",
                                                    textAlign: "center",
                                                }}
                                            />
                                        </div>
                                    </div>
                                    {/* Measurements */}
                                    <div className="cartItem">
                                        <input
                                            type="text"
                                            placeholder="e.g. 2m x 1.5m"
                                            value={material.measurements ?? ""}
                                            onChange={(e) =>
                                                handleMaterialMeasurements(material, e.target.value)
                                            }
                                            style={{
                                                width: "100%",
                                                padding: "6px 10px",
                                                border: "1.5px solid #e5e7eb",
                                                borderRadius: "6px",
                                                fontSize: "14px",
                                            }}
                                        />
                                    </div>
                                    {/* Quantity */}
                                    <div className="quantityItem" style={{ minHeight: 56, maxHeight: 56 }}>
                                        <Add
                                            style={{ fontSize: "1.75rem", cursor: "pointer" }}
                                            onClick={() => handleMaterialQuantity(material, 1, material.amount + 1)}
                                        />
                                        {material.amount}
                                        <Remove
                                            style={{ fontSize: "1.75rem", cursor: "pointer" }}
                                            onClick={() => handleMaterialQuantity(material, -1, Math.max(0, material.amount - 1))}
                                        />
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div style={{ padding: "40px 20px", textAlign: "center", color: "#6b7280" }}>
                                No materials added yet
                            </div>
                        )}
                    </div>
                    <div className="summary w-full flex lightblue">
                        <div className="summarysubtotal">Total Materials</div>
                        <div className="summarysubtotal">{materialsCart.length}</div>
                    </div>
                    <div className="summary w-full flex lightblue">
                        <div className="summarysubtotal">Total Amount</div>
                        <div className="summarysubtotal">RM {calculateTotal().toFixed(2)}</div>
                    </div>

                    {/* Form Inputs */}
                    <div className="possummaryContainer">
                        <div className="formGridContainer">
                            <div className="addProductItem">
                                <label>Date of Issue *</label>
                                <input
                                    type="date"
                                    value={formData.dateOfIssue}
                                    onChange={(e) => setFormData({ ...formData, dateOfIssue: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="addProductItem">
                                <label>Company *</label>
                                <Select
                                    styles={customSelectStyles}
                                    isSearchable={true}
                                    isClearable={true}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    value={
                                        formData.company
                                            ? companiesData
                                                  .map((company) => ({
                                                      label: company.name,
                                                      value: company._id,
                                                  }))
                                                  .find((opt) => opt.value === formData.company)
                                            : null
                                    }
                                    onChange={(selectedOption) => {
                                        setFormData({
                                            ...formData,
                                            company: selectedOption ? selectedOption.value : "",
                                        });
                                    }}
                                    options={companiesData.map((company) => ({
                                        label: company.name,
                                        value: company._id,
                                    }))}
                                    placeholder="Select Company"
                                />
                            </div>
                            <div className="addProductItem">
                                <label>Classification Code</label>
                                <input
                                    type="text"
                                    value={formData.classificationCode}
                                    onChange={(e) => setFormData({ ...formData, classificationCode: e.target.value })}
                                    placeholder="Enter classification code"
                                />
                            </div>
                            <div className="addProductItem">
                                <label>PO Number</label>
                                <input
                                    type="text"
                                    value={formData.poNumber}
                                    onChange={(e) => setFormData({ ...formData, poNumber: e.target.value })}
                                    placeholder="Leave blank to auto-generate"
                                />
                            </div>
                            <div className="addProductItem remarkField">
                                <label>Remarks</label>
                                <textarea
                                    value={formData.remarks}
                                    onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                                    placeholder="Additional notes..."
                                />
                            </div>
                        </div>
                    </div>

                    <div className="checkoutButtons">
                        <div
                            className="salesButton sales"
                            onClick={handleSubmit}
                        >
                            {isLoading ? "Creating..." : "Create Purchase Order"}
                        </div>
                    </div>
                    <div className="checkoutButtons">
                        <div
                            className="salesButton reset"
                            onClick={() => history.push("/purchaseOrders")}
                        >
                            Cancel
                        </div>
                    </div>
                </div>

                {/* Desktop: show Select Materials inline. Mobile: icon opens modal */}
                <div className={`posWrapper lightred purchase-order-select-materials-section ${isMobileNPO ? "purchase-order-select-materials-hidden" : ""}`}>
                    <div className="checkoutButtons">
                        <div className="salesButton bronze">
                            Select Materials
                        </div>
                    </div>
                    <div className="posContainer">
                        {categories ? (
                            <>
                                {categories.filter(category => {
                                    const excludedCategories = ["ACP", "Glass", "Metal Deck", "Polycarbonate", "Awning", "Add On"];
                                    return !excludedCategories.includes(category.title);
                                }).map((category, i) => (
                                    <div
                                        key={i}
                                        className="posItem"
                                        onClick={() => {
                                            setProduct(materialsData);
                                            setItemDetails({
                                                name: category.title,
                                                isMaterial: true,
                                            });
                                            window.scrollTo(0, 0);
                                            setColorPicker(true);
                                            if (isMobileNPO) setSelectMaterialsModalOpen(false);
                                        }}
                                    >
                                        <div className="productDetails">
                                            {category.title}
                                        </div>
                                    </div>
                                ))}
                                <div
                                    className="posItem"
                                    onClick={openAddOnModal}
                                >
                                    <div className="productDetails">Add On</div>
                                </div>
                            </>
                        ) : (
                            <div className="posItem">
                                <div className="productDetails">Loading categories...</div>
                            </div>
                        )}
                    </div>
                </div>
                {isMobileNPO && (
                    <>
                        <button
                            type="button"
                            className="purchase-order-select-materials-trigger"
                            onClick={() => setSelectMaterialsModalOpen(true)}
                            aria-label="Select materials"
                        >
                            <FiShoppingCart className="purchase-order-select-materials-trigger-icon" />
                            <span>Select Materials</span>
                        </button>
                        {selectMaterialsModalOpen && (
                            <>
                                <div
                                    className="purchase-order-select-materials-backdrop"
                                    onClick={() => setSelectMaterialsModalOpen(false)}
                                    aria-hidden="true"
                                />
                                <div className="purchase-order-select-materials-modal">
                                    <div className="purchase-order-select-materials-modal-header">
                                        <h3>Select Materials</h3>
                                        <button
                                            type="button"
                                            className="purchase-order-select-materials-modal-close"
                                            onClick={() => setSelectMaterialsModalOpen(false)}
                                            aria-label="Close"
                                        >
                                            <Close />
                                        </button>
                                    </div>
                                    <div className="purchase-order-select-materials-modal-body">
                                        <div className="checkoutButtons">
                                            <div className="salesButton bronze">
                                                Select Materials
                                            </div>
                                        </div>
                                        <div className="posContainer">
                                            {categories ? (
                                                <>
                                                    {categories.filter(category => {
                                                        const excludedCategories = ["ACP", "Glass", "Metal Deck", "Polycarbonate", "Awning", "Add On"];
                                                        return !excludedCategories.includes(category.title);
                                                    }).map((category, i) => (
                                                        <div
                                                            key={i}
                                                            className="posItem"
                                                            onClick={() => {
                                                                setProduct(materialsData);
                                                                setItemDetails({
                                                                    name: category.title,
                                                                    isMaterial: true,
                                                                });
                                                                window.scrollTo(0, 0);
                                                                setColorPicker(true);
                                                                setSelectMaterialsModalOpen(false);
                                                            }}
                                                        >
                                                            <div className="productDetails">
                                                                {category.title}
                                                            </div>
                                                        </div>
                                                    ))}
                                                    <div
                                                        className="posItem"
                                                        onClick={openAddOnModal}
                                                    >
                                                        <div className="productDetails">Add On</div>
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="posItem">
                                                    <div className="productDetails">Loading categories...</div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>
        </>
    );
};

export default NewPurchaseOrder;

